import isViewMandatory from '@opsbase/lib-data-view/helpers/isViewMandatory';
import DataType from '@opsbase/lib-datatype/DataType';
import doNothing from '@opsbase/lib-general/utils/doNothing';
import { getFromStringOrFunction } from '@opsbase/lib-general/utils/getFromStringOrFunction';
import i18n from '@opsbase/lib-i18n';
import useSelf from '@opsbase/react-ctrl-self/useSelf';
import useSelfActions from '@opsbase/react-ctrl-self/useSelfActions';
import useOrgFindById from '@opsbase/react-gql-hooks/organisation/useOrgFindById';
import useOrgUpdate from '@opsbase/react-gql-hooks/organisation/useOrgUpdate';
import useCallforward from '@opsbase/react-hooks/useCallforward';
import markdown from '@opsbase/react-markdown';
import AppPage from '@opsbase/web-app/AppPage';
import useViewRegistry from '@opsbase/web-ctrl-view/useViewRegistry';
import Card from '@opsbase/web-layout/Card';
import Vertical from '@opsbase/web-layout/Vertical';
import Radio from '@opsbase/web-ui/Radio';
import Switch from '@opsbase/web-ui/Switch';
import Text from '@opsbase/web-ui/Text';
import React, { useCallback } from 'react';
export function AdvancedOpReportingPage() {
    const self = useSelf();
    const { updateSettings } = useSelfActions();
    const { data: org } = useOrgFindById({ variables: { id: self.organisationId } });
    const updateOrg = useOrgUpdate();
    const handleChange = useCallback(patch => {
        updateOrg({
            id: org.id,
            patch,
        }, {
            optimisticResponse: {
                __typename: 'Mutation',
                updateOrg: {
                    __typename: 'Organisation',
                    ...org,
                    patch,
                },
            },
        })
            .then(updatedOrg => {
            updateSettings(updatedOrg.settings);
        })
            .catch(doNothing);
    }, [org, updateSettings, updateOrg]);
    const handleChangeMandatory = useCallback((viewIds) => {
        handleChange({ settings: {
                mandatoryOpsLogLmssIds: viewIds
            } });
    }, [self.settings, handleChange]);
    const handleChangeDefault = useCallforward((viewId) => {
        handleChange({ settings: {
                defaultOpsLogLmssId: viewId
            } });
    }, [self.settings, handleChange]);
    return (React.createElement(AppPage, null,
        React.createElement(Vertical, { padding: true, style: { overflow: 'auto' } },
            React.createElement(Vertical, { padding: true, gutter: true },
                React.createElement(Text, { variant: "h2" }, markdown(i18n.t('favourite-ops-savedviews'))),
                React.createElement(Card, { padding: true, style: { width: 500 } },
                    React.createElement(ReportsTable, { onChangeMandatory: handleChangeMandatory, onChangeDefault: handleChangeDefault, type: DataType.OP }))))));
}
export default AdvancedOpReportingPage;
function ReportsTable(props) {
    const { type, onChangeMandatory, onChangeDefault } = props;
    const views = useViewRegistry();
    const self = useSelf();
    const defaultId = type === DataType.OP
        ? self.settings.defaultOpsLogLmssId
        : self.settings.defaultIssuesLogLmssId;
    const mandatoryList = type === DataType.OP
        ? self.settings.mandatoryOpsLogLmssIds
        : self.settings.mandatoryIssuesLogLmssIds;
    const handleChangeMandatory = useCallforward(view => onOff => {
        const newMandatoryList = mandatoryList.filter(v => v != view.id);
        if (onOff)
            newMandatoryList.push(view.id);
        onChangeMandatory(newMandatoryList);
    }, [mandatoryList, onChangeMandatory]);
    const handleChangeDefault = useCallback(view => () => {
        onChangeDefault(view.id);
    }, [onChangeDefault]);
    const visibleViews = Object.values(views)
        .filter((view) => !view.isDashboard)
        .filter((view) => view.type === type)
        .map(v => {
        v.title = getFromStringOrFunction(v.title);
        return v;
    })
        .sort((a, b) => a.title.localeCompare(b.title));
    return (React.createElement("table", { style: { width: '100%', overflow: 'auto' } },
        React.createElement("tbody", null,
            React.createElement("tr", null,
                React.createElement("th", { style: { width: '100%' } }),
                React.createElement("th", { style: { padding: 4 } }, "Mandatory"),
                React.createElement("th", { style: { padding: 4 } }, "Default")),
            visibleViews.map((view) => {
                const viewIsMandatory = isViewMandatory(view, self);
                const isDefault = defaultId === view.id;
                return (React.createElement("tr", { key: view.id },
                    React.createElement("td", null,
                        React.createElement(Text, null, view.title)),
                    React.createElement("td", null,
                        React.createElement(Switch, { onChange: handleChangeMandatory(view), disabled: isDefault, checked: viewIsMandatory })),
                    React.createElement("td", null,
                        React.createElement(Radio, { onChange: handleChangeDefault(view), disabled: !viewIsMandatory, checked: isDefault }))));
            }))));
}
